import Swiper, { Navigation } from 'swiper'
import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants.js'

Swiper.use([Navigation])

const SELECTORS = {
  minecraftCarousel: '.js-minecraft-carousel',
  swiper: '.js-carousels__swiper',
  wrapper: '.js-minecraft-carousel__wrapper',
  scrimWrapper: '.js-swiper-wrapper',
  slide: '.js-swiper-slide',
  tabCloseModal: '.js-close-modal',
  navNext: '.js-swiper_nav--next',
  navPrev: '.js-swiper_nav--prev',
  hotspot: '.js-minecraft-carousel__hotspot',
  hotspotLabel: '.js-minecraft-carousel__hotspot__label--wrapper',
  hotspotButton: '.js-hotspot-button',
  hotspotContent: '.js-hotspot-content',
  modal: '.js-modal',
  closeHotspotButton: '.js-minecraft-carousel__hotspot__button--close',
  scrim: '.js-minecraft-carousel__scrim'
}

const FOCUSABLE_SELECTORS = [
  '.minecraft-carousel__modal__button--close',
  '.minecraft-carousel__modal__header',
  '.minecraft-carousel__modal__eyebrow',
  '.minecraft-carousel__modal__body'
].join(',')

class MinecraftCarousel {
  constructor (element) {
    this.element = element
    this.swiper = this.element.querySelector(SELECTORS.swiper)
    this.wrapper = this.element.querySelector(SELECTORS.wrapper)
    this.scrimWrapper = this.element.querySelector(SELECTORS.scrimWrapper)
    this.slide = this.element.querySelector(SELECTORS.slide)
    this.tabCloseModalArr = this.element.querySelectorAll(SELECTORS.tabCloseModal)
    this.navNext = this.element.querySelector(SELECTORS.navNext)
    this.navPrev = this.element.querySelector(SELECTORS.navPrev)
    this.hotspots = this.element.querySelectorAll(SELECTORS.hotspot)
    this.hotspotLabelsArr = this.element.querySelectorAll(SELECTORS.hotspotLabel)
    this.hotspotButtons = this.element.querySelectorAll(SELECTORS.hotspotButton)
    this.modalsArr = this.element.querySelectorAll(SELECTORS.hotspotContent)
    this.toggleModalsArr = this.element.querySelectorAll(SELECTORS.modal)
    this.closeHotspotButtonsArr = this.element.querySelectorAll(SELECTORS.closeHotspotButton)
    this.scrim = this.element.querySelector(SELECTORS.scrim)
    this.desktopQuery = window.matchMedia('(min-width: 768px)')
    this.isModalOpen = false
    this.focusableModalElements = []

    this.init()
  }

  init() {
    this.registerEvents()
  }

  registerEvents() {
    this.createCarousel()
    this.getCoords()
    this.setupModalEvents()

    // Array of modal elements to set and remove focus from when opening/closing the hotspot modals
    this.getFocusableSelectors()
    this.removeFocusableModalElements()
  }

  createCarousel() {
    this.minecraftCarousel = new Swiper(this.swiper, {
      direction: 'horizontal',
      slidesPerView: 1.05,
      centeredSlides: false,
      spaceBetween: 15,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1
      },
      freeMode: {
        enabled: true,
        sticky: true,
        momentumBounce: false
      },
      breakpoints: {
        768: {
          slidesPerView: 1.05,
          spaceBetween: 32
        }
      },
      a11y: false,
      navigation: {
        nextEl: this.navNext,
        prevEl: this.navPrev
      }
    })
  }

  // Create an array of the modal elements we want to hide while the modal is closed
  getFocusableSelectors() {
    this.focusableModalElements = Array.from(
      this.wrapper.querySelectorAll(FOCUSABLE_SELECTORS)
    )
  }

  // Remove tabindex='-1' from each element in the modal while it's open to add it back to the tab order
  addFocusableModalElements() {
    for (const elem of this.focusableModalElements) {
      elem.removeAttribute('tabindex')
    }

    // Give the outer modal div a tabindex='0' to allow focus to be tracked while open
    for (const elem of this.modalsArr) {
      elem.setAttribute('tabindex', '0')
    }

    // Give the last elem div a tabindex='0' to allow focus to be tracked while open
    for (const elem of this.tabCloseModalArr) {
      elem.setAttribute('tabindex', '0')
    }
  }

  // Add a tabindex='-1' to each element in the modal while it's closed
  removeFocusableModalElements = () => {
    for (const elem of this.focusableModalElements) {
      elem.setAttribute('tabindex', '-1')
    }

    // Remove tabindex from outer modal div when modal is closed
    for (const elem of this.modalsArr) {
      elem.removeAttribute('tabindex')
    }

    // Remove tabindex from last modal div when modal is closed
    for (const elem of this.tabCloseModalArr) {
      elem.removeAttribute('tabindex')
    }
  }

  setupModalEvents() {
    this.hotspotButtons.forEach((hotspot, index) => {
      const toggleModal = () => {
        this.toggleModalsArr[index].classList.toggle('-togglemodal')
        this.modalsArr[index].classList.toggle(GLOBAL_CONSTANTS.CLASSES.HIDE)
        this.hotspotLabelsArr[index].classList.toggle(GLOBAL_CONSTANTS.CLASSES.HIDE)
        this.scrim.classList.toggle(GLOBAL_CONSTANTS.CLASSES.HIDE)
        this.scrimWrapper.classList.toggle(GLOBAL_CONSTANTS.CLASSES.OPEN)
        this.slide.classList.toggle(GLOBAL_CONSTANTS.CLASSES.OPEN)

        if (this.isModalOpen) {
          this.removeFocusableModalElements()
          hotspot.focus()
          window.onscroll = () => {}
        } else {
          this.addFocusableModalElements()
          this.closeHotspotButtonsArr[index].focus()
          if (!this.desktopQuery.matches) {
            const x = window.pageXOffset || document.documentElement.scrollLeft
            const y = window.pageYOffset || document.documentElement.scrollTop
            window.onscroll = () => { window.scrollTo(x, y) }
          }
        }
        this.isModalOpen = !this.isModalOpen
      }

      // Esc key handler for closing modals
      const escKeyHandler = (event) => {
        if (event.key === 'Escape' && this.isModalOpen) {
          toggleModal()
        }
      }

      hotspot.addEventListener('click', toggleModal)
      this.closeHotspotButtonsArr[index].addEventListener('click', toggleModal)
      this.element.addEventListener('keydown', escKeyHandler, true)

      // Close the modal when the outer modal wrapper is focused
      this.modalsArr[index].addEventListener('focus', toggleModal)

      // Close the modal when the last element inside the modal is focused
      this.tabCloseModalArr[index].addEventListener('focus', toggleModal)
    })
  }

  // Gets the coordinate values from the data-x and data-y atrribute on the modal wrapper
  // Then adds the 'bottom' and 'left' styles to the modal wrapper to position them on the slide
  getCoords() {
    this.hotspots.forEach(hotspot => {
      const coordX = hotspot.dataset.x
      const coordY = hotspot.dataset.y
      hotspot.style.left = coordX + '%'
      hotspot.style.bottom = coordY + '%'
    })
  }
}

export default {
  selector: SELECTORS.minecraftCarousel,
  Source: MinecraftCarousel
}
